import { Link } from "@tanstack/react-router";
import { AlertTriangle, ChevronRight, Loader2 } from "lucide-react";
import { useShallow } from "zustand/shallow";
import { usePlaygroundStore } from "@/app/playground/execute/hooks/use-store";

const WORD_SEPARATOR_REGEX = /[_-]/;

const formatProviderName = (id: string) =>
  id
    .split(WORD_SEPARATOR_REGEX)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export function AuthAlert() {
  const { authStatus, authUrl, selectedTool } = usePlaygroundStore(
    useShallow((state) => ({
      authStatus: state.authStatus,
      authUrl: state.authUrl,
      selectedTool: state.selectedTool,
    }))
  );

  if (authStatus !== "pending") {
    return null;
  }

  const providerId =
    selectedTool?.requirements?.authorization?.provider_id ??
    selectedTool?.requirements?.authorization?.id ??
    null;
  const providerName = providerId ? formatProviderName(providerId) : null;

  return (
    <div
      aria-live="polite"
      className="w-full space-y-3 rounded-xl border border-warning border-l-4 bg-card px-5 py-4"
      role="alert"
    >
      <div className="flex items-center gap-2 text-foreground">
        <Loader2 className="size-5 flex-shrink-0 animate-spin text-warning" />
        <span className="font-semibold text-base">
          {providerName
            ? `Connect ${providerName}`
            : "Waiting for authorization"}
        </span>
      </div>

      <div className="space-y-3 text-muted-foreground">
        <p className="text-sm">
          We need your permission to run this tool. A popup should open to
          complete authorization.
        </p>
        {authUrl ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>If the popup did not open, continue here:</span>
            <a
              className="inline-flex items-center gap-2 rounded-md bg-link/10 px-3 py-1.5 font-semibold text-link hover:bg-link/20"
              href={authUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              Authorize in a new tab
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const PROVIDER_NOT_ACTIVE_REGEX = /provider(?:_| )not(?:_| )active/i;
const AUTH_REQUIRED_REGEX = /authorization required|unauthorized|401/i;
const AUTH_TIMEOUT_REGEX = /timeout|timed out|expired/i;

const getAuthErrorMessage = (
  authError: string | null,
  providerName: string | null
) => {
  if (!authError) {
    return providerName
      ? `Could not connect to ${providerName}.`
      : "Could not complete authorization.";
  }

  if (PROVIDER_NOT_ACTIVE_REGEX.test(authError)) {
    return providerName
      ? `${providerName} is not enabled for this workspace.`
      : "This provider is not enabled for this workspace.";
  }

  if (AUTH_TIMEOUT_REGEX.test(authError)) {
    return "Authorization timed out. Please try again.";
  }

  if (AUTH_REQUIRED_REGEX.test(authError)) {
    return "Authorization is required to run this tool.";
  }

  return "Could not complete authorization.";
};

export function AuthErrorTerminal() {
  const { authStatus, authError, selectedTool } = usePlaygroundStore(
    useShallow((state) => ({
      authStatus: state.authStatus,
      authError: state.authError,
      selectedTool: state.selectedTool,
    }))
  );

  if (authStatus !== "failed") {
    return null;
  }

  const providerId =
    selectedTool?.requirements?.authorization?.provider_id ??
    selectedTool?.requirements?.authorization?.id ??
    null;

  const providerName = providerId ? formatProviderName(providerId) : null;
  const isProviderNotActive = authError
    ? PROVIDER_NOT_ACTIVE_REGEX.test(authError)
    : false;
  const errorMessage = getAuthErrorMessage(authError ?? null, providerName);

  return (
    <div
      aria-live="polite"
      className="rounded-xl border border-red-500/30 bg-card px-4 py-4 text-sm"
      role="alert"
    >
      <div className="flex items-center gap-2 text-foreground">
        <AlertTriangle className="size-4 flex-shrink-0 text-red-400" />
        <span className="font-semibold">Authorization failed</span>
      </div>

      <p className="mt-2 text-muted-foreground">{errorMessage}</p>

      {isProviderNotActive ? (
        <div className="mt-3">
          <Link
            className="inline-flex items-center gap-1 rounded-md bg-link/10 px-3 py-1.5 font-semibold text-link hover:bg-link/20"
            from="/orgs/$organizationId/projects/$projectId/auth/oauth"
            search={providerId ? { name: providerId } : undefined}
            to="."
          >
            {`Enable ${providerName ?? "provider"}`}
          </Link>
        </div>
      ) : null}

      {authError ? (
        <details className="group mt-3">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-muted-foreground text-xs hover:text-foreground">
            <ChevronRight className="size-3 transition-transform group-open:rotate-90" />
            <span>View details</span>
          </summary>
          <div className="mt-2 rounded-lg border border-neutral-200 bg-neutral-100/60 px-3 py-2 font-mono text-neutral-500 text-sm dark:border-neutral-800/60 dark:bg-neutral-950/40 dark:text-neutral-400">
            {authError}
          </div>
        </details>
      ) : null}
    </div>
  );
}
